import React, { useEffect, useMemo, useState } from "react";
import { adsListFields } from "../../services/api";
import { formatFieldValue, readField, ORIGIN_META, ORIGIN_ORDER } from "./FieldPicker";

/**
 * Everything else we hold about one ad.
 *
 * Deliberately excludes whatever the summary above already shows: repeating
 * status, dates and body text made the drawer twice as long and taught the
 * reader to scroll past the section rather than read it.
 *
 * Values are laid out by length, not in one fixed column: short ones tile, long
 * text and JSON get the full width and their own scroll. Grouping is by origin,
 * because a provider's figure and a model's reading of a picture are different
 * kinds of claim.
 */

// Palette and order shared with the picker and the filters.

/** Fields the curated summary and the creative block already render. */
const ALREADY_SHOWN = new Set([
  "external_ad_id", "source", "advertiser_ref", "status", "start_date", "end_date",
  "topic", "total_days_shown", "headline", "primary_text", "description", "caption",
  "call_to_action", "landing_url", "creative_type", "media_url",
  // Meta's key fields, now shown in Ad info.
  "eu_total_reach", "publisher_platforms", "target_ages", "target_gender",
  "target_locations", "languages", "beneficiary_payers", "advertiser_name",
]);

const LONG = 70;

/** Never render a live access token into the page. */
function safeText(text) {
  if (typeof text !== "string") return text;
  return text.replace(/([?&]access_token=)[^&\s]+/gi, "$1[hidden]");
}

function ValueCell({ field, value }) {
  const [open, setOpen] = useState(false);
  const raw = formatFieldValue(value, field.kind);
  const text = safeText(raw);
  const isLong = typeof text === "string" && text.length > LONG;
  const mono = field.kind === "json" || isLong;

  return (
    <div
      title={field.note || field.key}
      style={{
        gridColumn: isLong ? "1 / -1" : "auto",
        border: "1px solid #eef2f7", borderRadius: 8, padding: "7px 10px",
        background: "#fff", minWidth: 0,
      }}
    >
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".03em", textTransform: "uppercase" }}>
        {field.label}
      </div>
      <div
        style={{
          fontSize: mono ? 11.5 : 13, color: text ? "#111827" : "#cbd5e1",
          fontFamily: mono ? "ui-monospace, Menlo, Consolas, monospace" : "inherit",
          wordBreak: "break-word", whiteSpace: "pre-wrap", marginTop: 2,
          maxHeight: open ? 320 : 62, overflowY: open ? "auto" : "hidden",
        }}
      >
        {text || "—"}
      </div>
      {isLong && (
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            border: "none", background: "none", padding: 0, marginTop: 3,
            color: "#6366f1", fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

export default function AdvancedFieldsPanel({ ad }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || catalog.length || !ad?.source) return undefined;
    let cancelled = false;
    adsListFields(ad.source)
      .then(d => { if (!cancelled) setCatalog(d.fields || []); })
      .catch(e => { if (!cancelled) setError(e?.message || "Could not load the field list"); });
    return () => { cancelled = true; };
  }, [open, catalog.length, ad?.source]);

  const rows = useMemo(() => {
    if (!ad) return [];
    return catalog
      .filter(f => !ALREADY_SHOWN.has(f.key))
      .map(f => ({ field: f, value: readField(ad, f) }));
  }, [catalog, ad]);

  const populated = rows.filter(r => r.value !== null && r.value !== undefined && r.value !== "");
  const visible = showEmpty ? rows : populated;

  const byOrigin = useMemo(() => {
    const out = {};
    for (const r of visible) (out[r.field.origin] = out[r.field.origin] || []).push(r);
    return out;
  }, [visible]);

  if (!ad) return null;

  return (
    <section>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 9, width: "100%",
          background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10,
          padding: "10px 14px", cursor: "pointer", fontSize: 13.5, fontWeight: 800,
          color: "#111827", textAlign: "left",
        }}
      >
        <span style={{ fontSize: 12, color: "#6b7280" }}>{open ? "▾" : "▸"}</span>
        Advanced fields
        {catalog.length > 0 && (
          <span style={{ fontWeight: 600, color: "#6b7280", fontSize: 12 }}>
            {populated.length} with a value · {rows.length} available
          </span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
          {error && <div style={{ fontSize: 12.5, color: "#b91c1c" }}>{error}</div>}
          {!error && catalog.length === 0 && (
            <div style={{ fontSize: 12.5, color: "#9ca3af" }}>Loading fields…</div>
          )}

          {catalog.length > 0 && (
            <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12.5, color: "#4b5563" }}>
              <input
                id={`adv-show-empty-${ad.id}`}
                type="checkbox"
                checked={showEmpty}
                onChange={e => setShowEmpty(e.target.checked)}
                style={{ accentColor: "#6366f1", cursor: "pointer" }}
              />
              Show fields with no value
            </label>
          )}

          {ORIGIN_ORDER.filter(o => (byOrigin[o] || []).length).map(origin => {
            const meta = ORIGIN_META[origin];
            return (
              <div key={origin}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 999, background: meta.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: meta.color }}>{meta.label}</span>
                  <span style={{ fontSize: 10.5, color: "#9ca3af" }}>{meta.hint}</span>
                  <span style={{ fontSize: 10.5, color: "#cbd5e1", marginLeft: "auto" }}>
                    {byOrigin[origin].length}
                  </span>
                </div>
                {/* Tiles so a wide drawer uses its width instead of stacking. */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
                  gap: 6, alignItems: "start",
                }}>
                  {byOrigin[origin].map(({ field: f, value }) => (
                    <ValueCell key={f.key} field={f} value={value} />
                  ))}
                </div>
              </div>
            );
          })}

          {catalog.length > 0 && visible.length === 0 && (
            <div style={{ fontSize: 12.5, color: "#9ca3af" }}>
              Every remaining field is empty for this ad.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
